import {
  Box,
  HStack,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { TestingReportUpdateMetadata } from "gen/model/testingReportUpdateMetadata";
import { TestingReportUpdateRequestDataRequest } from "gen/model/testingReportUpdateRequestDataRequest";
import { TFunction } from "i18next";
import { noop } from "lodash-es";
import React from "react";
import {
  Control,
  FieldArrayWithId,
  useFieldArray,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  useForm,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link as RouteLink, useNavigate } from "react-router-dom";
import Form from "shared/components/form/Form";
import { FormSimpleTextInput } from "shared/components/input/text/SimpleTextInput";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import {
  TableAddButton,
  TableRemoveButton,
} from "shared/components/table/TableRowActionButtons";
import { ArrowUpRightIcon } from "shared/icons/ArrowUpRightIcon";
import { rootRoute } from "shared/navigation/Routes";

const FULL_SHA_LENGTH = 40;

export const createUpdateCommitDataRow = () => ({
  commit: "",
  notes: "",
});

const FormInputCommitRow: React.FC<{
  control: Control<TestingReportUpdateRequestDataRequest>;
  index: number;
  row: FieldArrayWithId<
    TestingReportUpdateRequestDataRequest,
    "metadata.commit_rows"
  >;
  append: UseFieldArrayAppend<
    TestingReportUpdateRequestDataRequest,
    "metadata.commit_rows"
  >;
  remove: UseFieldArrayRemove;
}> = ({ control, row, index, append, remove }) => {
  const commit = useWatch({
    control,
    name: `metadata.commit_rows.${index}.commit`,
  });

  const isFullSha = !!commit && commit.length === FULL_SHA_LENGTH;
  return (
    <Tr key={row.id}>
      <Td>
        <TableAddButton onClick={() => append(createUpdateCommitDataRow())} />
      </Td>
      <Td>
        <HStack>
          <FormSimpleTextInput
            backgroundColor={isFullSha ? "primary.100" : undefined}
            name={`metadata.commit_rows.${index}.commit`}
            placeholder="073c0130c391bec05e5dc86657027cb43a02770a"
          />
          {isFullSha && (
            <RouteLink
              to={`https://github.com/Maybern/maybern/commit/${commit}`}
            >
              <ArrowUpRightIcon className="mb-metadata-item-link-icon" />
            </RouteLink>
          )}
        </HStack>
      </Td>
      <Td>
        <FormSimpleTextInput
          name={`metadata.commit_rows.${index}.notes`}
          placeholder="This commit cause the following tests to fail ... because ..."
        />
      </Td>
      <Td>{!!index && <TableRemoveButton onClick={() => remove(index)} />}</Td>
    </Tr>
  );
};

export const InternalIntegrationTestingUpdateNotesFormTable: React.FC<{
  control: Control<TestingReportUpdateRequestDataRequest>;
  t: TFunction<"internal", "common">;
  updatedTestingReportId?: string;
}> = ({ control, t, updatedTestingReportId }) => {
  const navigate = useNavigate();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "metadata.commit_rows",
  });
  return (
    <TableBox>
      <TableBoxHeader>
        <HStack>
          <TableBoxTitle>Notes</TableBoxTitle>
          {updatedTestingReportId && (
            <Link
              size="sm"
              onClick={() =>
                navigate(
                  rootRoute({}).internal({}).integrationTesting({}).report({
                    integrationTestingReportId: updatedTestingReportId,
                  }).$,
                )
              }
            >
              Updating Report
            </Link>
          )}
        </HStack>
      </TableBoxHeader>
      <TableBoxBody>
        <TableContainer height="100%" overflowY="auto">
          <Table variant="form">
            <Thead>
              <Tr>
                <Th width={0} />
                <Th w="250px">Commit</Th>
                <Th>{t("common:notes")}</Th>
                <Th width={0} />
              </Tr>
            </Thead>
            <Tbody>
              {fields.map((row, index) => (
                <FormInputCommitRow
                  key={row.id}
                  control={control}
                  index={index}
                  row={row}
                  append={append}
                  remove={remove}
                />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </TableBoxBody>
      <Box margin={4}>
        <FormTextArea
          title={t("common:additionalInformation")}
          name="metadata.notes"
        />
      </Box>
    </TableBox>
  );
};

export const InternalIntegrationTestingUpdateNotesReadOnlyFormTable: React.FC<{
  testingReportUpdateMetadata: TestingReportUpdateMetadata;
}> = ({ testingReportUpdateMetadata }) => {
  const { t } = useTranslation(["internal", "common"]);

  const form = useForm<TestingReportUpdateRequestDataRequest>({
    defaultValues: {
      metadata: testingReportUpdateMetadata,
    },
  });
  return (
    <Form form={form} onSubmit={noop} isDisabled={true}>
      <InternalIntegrationTestingUpdateNotesFormTable
        control={form.control}
        t={t}
        updatedTestingReportId={testingReportUpdateMetadata.testing_report_id}
      />
    </Form>
  );
};
